import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Typography,
  Divider,
} from "@mui/material";
import { AccountCircle, Menu as MenuIcon } from "@mui/icons-material";
import TranslateIcon from "@mui/icons-material/Translate";
import Grocerylogo from "../images/Grocerylogo.png";
import useLiveLocation from "./useLiveLocation";

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const { location, loading } = useLiveLocation();

  // ✅ Google Translate load
  useEffect(() => {
    const addGoogleTranslateScript = () => {
      if (document.getElementById("google-translate-script")) return;

      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      document.body.appendChild(script);

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,mr",
            autoDisplay: false,
          },
          "google_translate_element"
        );
      };
    };

    addGoogleTranslateScript();
  }, []);

  // ✅ Language change trigger
  const handleLanguageChange = (lang) => {
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = lang;
      select.dispatchEvent(new Event("change"));
    }
    setLangAnchor(null);
  };

  return (
    <Box sx={{ flexGrow: 1, mb: 8 }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          bgcolor: "#1b5e20",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* ✅ Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center" }}>
            <img src={Grocerylogo} alt="Logo" style={{ height: 50 }} />
          </Link>

          {/* ✅ Desktop View */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Location */}
              <Button
                sx={{
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: "14px",
                  textTransform: "none",
                  px: 1,
                }}
              >
                📍{" "}
                {loading ? (
                  <CircularProgress size={16} sx={{ color: "white", ml: 1 }} />
                ) : (
                  <Box component="span" sx={{ ml: 1 }}>
                    {location || "Set Location"}
                  </Box>
                )}
              </Button>

              {/* Login */}
              <Button
                variant="text"
                component={Link}
                to="/MyAccountsSignIn"
                startIcon={<AccountCircle />}
                sx={{
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: "bold",
                  borderRadius: "20px",
                }}
              >
                Login
              </Button>

              {/* Language Switcher */}
              <div style={{ display: "none" }} id="google_translate_element" />
              <IconButton
                color="inherit"
                onClick={(e) => setLangAnchor(e.currentTarget)}
              >
                <TranslateIcon />
              </IconButton>
              <Menu
                anchorEl={langAnchor}
                open={Boolean(langAnchor)}
                onClose={() => setLangAnchor(null)}
              >
                <MenuItem onClick={() => handleLanguageChange("en")}>
                  English
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange("mr")}>
                  Marathi
                </MenuItem>
              </Menu>
            </Box>
          )}

          {/* ✅ Mobile View */}
          {isMobile && (
            <>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{
                  sx: { width: 200 },
                }}
              >
                <MenuItem
                  disabled
                  sx={{ opacity: 1, fontWeight: "bold", color: "green" }}
                >
                  Options
                </MenuItem>
                <Divider />

                <MenuItem>
                  📍{" "}
                  {loading ? (
                    <CircularProgress size={16} sx={{ color: "green", ml: 1 }} />
                  ) : (
                    <Typography sx={{ ml: 1 }}>
                      {location || "Set Location"}
                    </Typography>
                  )}
                </MenuItem>

                <MenuItem
                  component={Link}
                  to="/MyAccountsSignIn"
                  onClick={() => setMenuAnchor(null)}
                >
                  <AccountCircle sx={{ mr: 1 }} /> Login
                </MenuItem>

                <Divider />
                <MenuItem onClick={() => handleLanguageChange("en")}>
                  English
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange("mr")}>
                  Marathi
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Header;
